import { ACTIVITY_TYPE_TO_TAG, ACTIVITY_TYPE_TO_TYPE, parseActivityOutcome } from '@/lib/activity-type-maps';
import { graphqlQuery } from '@/lib/api/graphql';
import type { GraphQLCursorConnection } from '@/lib/graphql-pagination';
import type { CaseUnifiedActivity } from './useCaseRecentActivities';
import { useCursorPagination } from './useCursorPagination';

// --- GraphQL types ---

interface CaseRecentActivityGraphQLNode {
  activityId: number;
  activityType: string;
  title: string;
  description: string;
  metadataJson: string | null;
  actorName: string;
  createdAt: string;
}

interface CaseRecentActivityListGraphQLResponse {
  collectionCases: {
    caseRecentActivityList: GraphQLCursorConnection<CaseRecentActivityGraphQLNode>;
  };
}

// --- GraphQL query ---

const CASE_RECENT_ACTIVITY_LIST_QUERY = `
  query CaseRecentActivityList($first: Int, $after: String, $filter: CaseRecentActivityListFilterInput) {
    collectionCases {
      caseRecentActivityList(first: $first, after: $after, filter: $filter) {
        edges {
          node {
            activityId
            activityType
            title
            description
            metadataJson
            actorName
            createdAt
          }
        }
        pageInfo {
          endCursor
          hasNextPage
        }
      }
    }
  }
`;

const ACTIVITY_PAGE_SIZE = 25;

// --- Mapping ---

function mapGraphQLNodeToCaseUnifiedActivity(node: CaseRecentActivityGraphQLNode): CaseUnifiedActivity {
  const tag = ACTIVITY_TYPE_TO_TAG[node.activityType] ?? 'Activity';
  const type = (ACTIVITY_TYPE_TO_TYPE[node.activityType] ?? 'system') as CaseUnifiedActivity['type'];

  return {
    id: String(node.activityId),
    type,
    tag,
    title: node.title,
    description: node.description,
    outcome: parseActivityOutcome(node.metadataJson),
    createdBy: node.actorName,
    createdAt: new Date(node.createdAt),
  };
}

// --- Mock data ---

function buildMockActivities(): CaseUnifiedActivity[] {
  const items: CaseUnifiedActivity[] = [
    {
      id: 'mock-action-1',
      type: 'status_change',
      tag: 'Activity',
      title: 'Case status changed to Active',
      description: 'Case status changed to Active',
      createdBy: 'Sarah Johnson',
      createdAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000),
    },
    {
      id: 'mock-action-2',
      type: 'call',
      tag: 'Communication',
      title: 'Outbound call made to customer',
      description: 'Attempted phone call - no answer',
      outcome: 'Left voicemail requesting callback',
      createdBy: 'Sarah Johnson',
      createdAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000),
    },
    {
      id: 'mock-action-3',
      type: 'email',
      tag: 'Communication',
      title: 'Payment reminder email sent',
      description: 'Sent payment reminder email',
      outcome: 'Email delivered successfully',
      createdBy: 'System',
      createdAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000),
    },
    {
      id: 'mock-note-1',
      type: 'note',
      tag: 'Note',
      title: 'Note Added',
      description: 'Customer requested payment plan options. Sent payment plan proposal.',
      createdBy: 'Mike Chen',
      createdAt: new Date(Date.now() - 4 * 24 * 60 * 60 * 1000),
    },
    {
      id: 'mock-note-2',
      type: 'note',
      tag: 'Note',
      title: 'Note Added',
      description: 'Spoke with accounts payable manager. Promised payment by end of week.',
      createdBy: 'Sarah Johnson',
      createdAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000),
    },
    {
      id: 'mock-note-3',
      type: 'note',
      tag: 'Note',
      title: 'Note Added',
      description: 'Case assigned to collections team. Customer has been unresponsive to previous payment reminders.',
      createdBy: 'System',
      createdAt: new Date(Date.now() - 6 * 24 * 60 * 60 * 1000),
    },
  ];
  return [...items].sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
}

// --- Hook ---

export interface CaseRecentActivitiesGraphQLResult {
  activities: CaseUnifiedActivity[];
  isLoading: boolean;
  isFetchingMore: boolean;
  hasMore: boolean;
  loadMore: () => void;
  reset: () => void;
  refetch: () => void;
}

export function useCaseRecentActivitiesGraphQL(caseId: string | undefined): CaseRecentActivitiesGraphQLResult {
  const result = useCursorPagination<CaseRecentActivityGraphQLNode, CaseUnifiedActivity>(
    {
      pageSize: ACTIVITY_PAGE_SIZE,
      buildMockData: buildMockActivities,
      executeQuery: async (apiConfig, afterCursor, signal) => {
        if (!caseId) {
          return { edges: [], pageInfo: { endCursor: null, hasNextPage: false } };
        }
        const variables: Record<string, unknown> = {
          first: ACTIVITY_PAGE_SIZE,
          filter: { collectionCaseId: { eq: Number(caseId) } },
        };
        if (afterCursor) {
          variables.after = afterCursor;
        }
        const response = await graphqlQuery<CaseRecentActivityListGraphQLResponse>(
          apiConfig,
          CASE_RECENT_ACTIVITY_LIST_QUERY,
          variables,
          signal,
        );
        return response.collectionCases.caseRecentActivityList;
      },
      mapNode: mapGraphQLNodeToCaseUnifiedActivity,
      postProcess: (items) => [...items].sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime()),
    },
    [caseId],
  );

  return {
    activities: result.items,
    isLoading: result.isLoading,
    isFetchingMore: result.isFetchingMore,
    hasMore: result.hasMore,
    loadMore: result.loadMore,
    reset: result.reset,
    refetch: result.refetch,
  };
}
